import { StateCreator } from "zustand";
import { designerPresetsEqual } from "@/lib/designer/actionProjection";
import { getWidgetPresets } from "@/lib/agent/widgetPresets";
import type { DesignerChoice } from "@/lib/designer/contracts";
import {
  AgentActionPlan,
  AgentComposerRecovery,
  AgentMessage,
  AgentPhase,
  AgentThinkingTraceStep,
  AgentWidget,
  DesignBrief,
  DressConcept,
  PresetOption,
} from "@/types/createAgent";
import {
  applyDesignDetailEdit,
  type EditableDesignDetailField,
} from "@/lib/designer/designDetails";
export type { EditableDesignDetailField } from "@/lib/designer/designDetails";
import type { CreatePageStore } from "../createPageStore";

function stablePendingMessageHash(seed: string): string {
  let first = 0x811c9dc5;
  let second = 0x9e3779b9;
  for (let index = 0; index < seed.length; index++) {
    const code = seed.charCodeAt(index);
    first = Math.imul(first ^ code, 0x01000193);
    second = Math.imul(second ^ code, 0x85ebca6b);
  }
  return `${(first >>> 0).toString(36)}${(second >>> 0).toString(36)}`;
}

export function createPendingSubmittedMessageId(seed?: string): string {
  if (seed) return `pending-legacy-${stablePendingMessageHash(seed)}`;
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to a browser-local uniqueness fallback.
  }
  return `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export interface AgentActions {
  // Conversation actions
  addMessage: (message: Omit<AgentMessage, "id" | "timestamp">) => void;
  removeMessage: (messageId: string) => void;
  updateMessageContent: (messageId: string, content: string) => void;
  setMessagePresets: (messageId: string, presets: PresetOption[]) => void;
  setMessageWidgets: (messageId: string, widgets: AgentWidget[]) => void;
  setMessageSources: (
    messageId: string,
    sources: AgentMessage["sources"] | undefined
  ) => void;
  setMessageThinkingTrace: (
    messageId: string,
    steps: AgentThinkingTraceStep[]
  ) => void;
  /** Records the user's pick on the message's widgets so the selection
   * receipt survives reloads — widgets round-trip through the persisted
   * conversation history, unlike client-only selection state. */
  markWidgetSelection: (
    messageId: string,
    selectedLabel: string,
    choice?: DesignerChoice
  ) => void;
  setTyping: (isTyping: boolean) => void;
  setThinkingStatus: (status: string | null) => void;
  setThinkingTrace: (steps: AgentThinkingTraceStep[]) => void;
  setPresetsLoading: (loading: boolean) => void;
  setAgentError: (error: string | null) => void;
  setExactSessionRestoreError: (error: string | null) => void;
  clearConversation: () => void;
  restoreMessages: (messages: AgentMessage[]) => void;

  // Phase and presets
  setPhase: (phase: AgentPhase) => void;
  setPresets: (presets: PresetOption[]) => void;

  // Session
  setSessionId: (sessionId: string | null) => void;
  setSessionVersions: (versions: {
    sessionVersion: number;
    messageVersion: number;
  }) => void;
  setAgentBrainState: (state: {
    designBrief?: DesignBrief | null;
    activeConcept?: DressConcept | null;
    actionPlan?: AgentActionPlan | null;
  }) => void;
  updateDesignDetail: (field: EditableDesignDetailField, value: string) => void;
  stageBackChoice: (value: string) => void;
  setActiveRequestController: (controller: AbortController | null) => void;
  abortActiveRequest: () => void;
  setEntryTransition: (transition: "idle" | "starting" | "settled") => void;
  beginCustomEntryChat: (composerSeed: string) => void;
  clearPendingComposerSeed: () => void;
  setPendingComposerSeed: (composerSeed: string | null) => void;
  setPendingComposerRecovery: (recovery: AgentComposerRecovery | null) => void;
  setPendingSubmittedMessage: (message: string | null) => void;
}

export const createAgentSlice: StateCreator<
  CreatePageStore,
  [["zustand/immer", never]],
  [],
  AgentActions
> = set => ({
  addMessage: message =>
    set((state: CreatePageStore) => {
      const newMessage: AgentMessage = {
        ...message,
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
      };
      state.agent.conversation.messages.push(newMessage);
    }),

  removeMessage: messageId =>
    set((state: CreatePageStore) => {
      state.agent.conversation.messages =
        state.agent.conversation.messages.filter(
          message => message.id !== messageId
        );
    }),

  updateMessageContent: (messageId, content) =>
    set((state: CreatePageStore) => {
      const message = state.agent.conversation.messages.find(
        msg => msg.id === messageId
      );
      if (message) {
        // Only update content, never change the role
        message.content = content;
      }
    }),

  setMessagePresets: (messageId, presets) =>
    set((state: CreatePageStore) => {
      const message = state.agent.conversation.messages.find(
        msg => msg.id === messageId
      );
      if (message && message.role === "assistant") {
        message.presets = presets;
      }
    }),

  setMessageWidgets: (messageId, widgets) =>
    set((state: CreatePageStore) => {
      const message = state.agent.conversation.messages.find(
        msg => msg.id === messageId
      );
      if (message && message.role === "assistant") {
        message.widgets = widgets;
      }
    }),

  setMessageSources: (messageId, sources) =>
    set((state: CreatePageStore) => {
      const message = state.agent.conversation.messages.find(
        msg => msg.id === messageId
      );
      if (message && message.role === "assistant") {
        message.sources = sources;
      }
    }),

  setMessageThinkingTrace: (messageId, steps) =>
    set((state: CreatePageStore) => {
      const message = state.agent.conversation.messages.find(
        msg => msg.id === messageId
      );
      if (message && message.role === "assistant") {
        message.thinkingTrace = steps;
      }
    }),

  markWidgetSelection: (messageId, selectedLabel, choice) =>
    set((state: CreatePageStore) => {
      const message = state.agent.conversation.messages.find(
        msg => msg.id === messageId
      );
      if (!message || message.role !== "assistant" || !message.widgets) {
        return;
      }
      const targetWidget = choice
        ? message.widgets.find(
            widget =>
              widget.active === true &&
              (!choice.source.widgetId ||
                (widget.id === choice.source.widgetId &&
                  widget.version === choice.source.widgetVersion)) &&
              getWidgetPresets(widget).some(preset =>
                designerPresetsEqual(preset, choice.preset)
              )
          )
        : null;
      if (choice && !targetWidget) return;
      message.widgets = message.widgets.map(widget => {
        const data = { ...widget.selection?.data };
        if (choice && widget.id !== targetWidget?.id) {
          delete data.selectedLabel;
          delete data.selectedPreset;
        } else {
          data.selectedLabel = selectedLabel;
          if (choice) data.selectedPreset = choice.preset;
        }
        return { ...widget, selection: { ...widget.selection, data } };
      });
    }),

  setTyping: isTyping =>
    set((state: CreatePageStore) => {
      state.agent.conversation.isTyping = isTyping;
    }),

  setThinkingStatus: status =>
    set((state: CreatePageStore) => {
      state.agent.conversation.thinkingStatus = status;
    }),

  setThinkingTrace: steps =>
    set((state: CreatePageStore) => {
      state.agent.conversation.thinkingTrace = steps;
    }),

  setPresetsLoading: loading =>
    set((state: CreatePageStore) => {
      state.agent.conversation.presetsLoading = loading;
    }),

  setAgentError: (error: string | null) =>
    set((state: CreatePageStore) => {
      state.agent.conversation.error = error;
    }),

  setExactSessionRestoreError: (error: string | null) =>
    set((state: CreatePageStore) => {
      state.agent.exactSessionRestoreError = error;
    }),

  clearConversation: () =>
    set((state: CreatePageStore) => {
      state.agent.conversation.messages = [];
      state.agent.conversation.error = null;
      state.agent.conversation.thinkingStatus = null;
      state.agent.conversation.thinkingTrace = [];
      state.agent.messageVersion = 0;
      state.agent.designBrief = null;
      state.agent.activeConcept = null;
      state.agent.actionPlan = null;
      state.agent.entryTransition = "idle";
      state.agent.entryChatActive = false;
      state.agent.pendingComposerSeed = null;
      state.agent.pendingComposerRecovery = null;
      state.agent.pendingSubmittedMessage = null;
      state.agent.pendingSubmittedMessageId = null;
      state.agent.exactSessionRestoreError = null;
    }),

  setEntryTransition: transition =>
    set((state: CreatePageStore) => {
      state.agent.entryTransition = transition;
    }),

  beginCustomEntryChat: composerSeed =>
    set((state: CreatePageStore) => {
      state.agent.entryTransition = "starting";
      state.agent.entryChatActive = true;
      state.agent.pendingComposerSeed = composerSeed;
    }),

  clearPendingComposerSeed: () =>
    set((state: CreatePageStore) => {
      state.agent.pendingComposerSeed = null;
    }),

  setPendingComposerSeed: composerSeed =>
    set((state: CreatePageStore) => {
      state.agent.pendingComposerSeed = composerSeed?.trim() || null;
    }),

  setPendingComposerRecovery: recovery =>
    set((state: CreatePageStore) => {
      state.agent.pendingComposerRecovery = recovery;
    }),

  setPendingSubmittedMessage: message =>
    set((state: CreatePageStore) => {
      const pendingMessage = message?.trim() || null;
      if (!pendingMessage) {
        state.agent.pendingSubmittedMessage = null;
        state.agent.pendingSubmittedMessageId = null;
        return;
      }
      if (
        state.agent.pendingSubmittedMessage !== pendingMessage ||
        !state.agent.pendingSubmittedMessageId
      ) {
        state.agent.pendingSubmittedMessageId =
          createPendingSubmittedMessageId();
      }
      state.agent.pendingSubmittedMessage = pendingMessage;
    }),

  restoreMessages: messages =>
    set((state: CreatePageStore) => {
      // Restore messages to conversation state (single source of truth)
      state.agent.conversation.messages = messages;
      if (messages.length > 0) {
        state.agent.entryTransition = "settled";
        state.agent.entryChatActive = false;
        // A save-point/checkout restoration can hydrate the authoritative
        // transcript before an uncertain local turn is reconciled. Keep that
        // explicit retry context until the transport owner confirms whether
        // the server already persisted it.
        if (!state.agent.pendingComposerRecovery) {
          state.agent.pendingComposerSeed = null;
        }
      }
    }),

  setPhase: phase =>
    set((state: CreatePageStore) => {
      state.agent.phase = phase;
    }),

  setPresets: presets =>
    set((state: CreatePageStore) => {
      // Set presets (single source of truth)
      state.agent.presets = presets;
    }),

  setSessionId: sessionId =>
    set((state: CreatePageStore) => {
      // Initial session minting (null -> id) establishes the current project;
      // replacing or clearing an existing id crosses a project boundary.
      if (state.agent.sessionId && state.agent.sessionId !== sessionId) {
        state.projectActionEpoch += 1;
      }
      state.agent.sessionId = sessionId;
      if (!sessionId) {
        state.agent.sessionVersion = 0;
        state.agent.messageVersion = 0;
      }
    }),

  setSessionVersions: versions =>
    set((state: CreatePageStore) => {
      state.agent.sessionVersion = Math.max(0, versions.sessionVersion);
      state.agent.messageVersion = Math.max(0, versions.messageVersion);
    }),

  setAgentBrainState: brainState =>
    set((state: CreatePageStore) => {
      if ("designBrief" in brainState) {
        state.agent.designBrief = brainState.designBrief ?? null;
      }
      if ("activeConcept" in brainState) {
        state.agent.activeConcept = brainState.activeConcept ?? null;
      }
      if ("actionPlan" in brainState) {
        state.agent.actionPlan = brainState.actionPlan ?? null;
      }
    }),

  updateDesignDetail: (field, rawValue) =>
    set((state: CreatePageStore) => {
      applyDesignDetailEdit(state, field, rawValue);
    }),

  stageBackChoice: rawValue =>
    set((state: CreatePageStore) => {
      applyDesignDetailEdit(state, "back", rawValue);
      // Selecting a card stages a canonical draft but never approves Front or
      // requests a paid Back render. Keep this atomic for store subscribers.
      state.form.data.frontApproved = false;
      state.form.data.generateBack = false;
    }),

  setActiveRequestController: controller =>
    set((state: CreatePageStore) => {
      state.agent.activeRequestController = controller;
    }),

  abortActiveRequest: () =>
    set((state: CreatePageStore) => {
      if (state.agent.activeRequestController) {
        state.agent.activeRequestController.abort();
      }
      state.agent.activeRequestController = null;
    }),
});
